import {
  isAnswerComplete,
  scoreMultipleChoice
} from "@pathwright/blocks-core/src/blocks/question/type/completion"
import {
  BLOCK_INPUT_STATES,
  useBlockCompletion,
  useBlocksConfig
} from "@pathwright/blocks-engine"
import { BlockText } from "@pathwright/blocks/src/shared"
import Checkbox from "@pathwright/ui/src/components/form/form-checkbox/Checkbox"
import RadioButton from "@pathwright/ui/src/components/form/form-radio/RadioButton"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import { useUIThemeContext } from "@pathwright/ui/src/components/ui/PathwrightUI"
import React, { useEffect } from "react"
import styled from "styled-components"
import { FeedbackIndicator, QuestionOptionCardStyles, SCORING_RED } from "../ui"
import {
  getMCExplanationFeedback,
  getQuestionOptions,
  getQuestionSelections,
  getResponseFeedbackType,
  isMultipleChoice,
  isSelected,
  normalizeMCOrdering,
  updateAnswerOnSelect
} from "../utils"

const OptionViewRow = styled.div`
  ${({ isChecked, checkedColor }) =>
    QuestionOptionCardStyles({ isChecked, checkedColor })}
  padding-left: 10px;

  & > label {
    // Match edit mode (tools add row height)
    padding: 1.5px 0 !important;
  }

  .BlockText p,
  .ContentText {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .RadioButton,
  .Checkbox {
    height: auto;

    & > span:last-of-type {
      display: inline-block;
    }
  }

  input[disabled] {
    cursor: not-allowed;
  }
`

const ExplanationWrapper = styled.div`
  .BlockText,
  .BlockText p {
    color: ${SCORING_RED} !important;
    font-size: 0.9em !important;
    font-style: italic;
  }

  .BlockText .katex {
    color: ${SCORING_RED};
  }
`

export const ExplanationFeedback = ({ label }) => (
  <ExplanationWrapper>
    <BlockText html={label} />
  </ExplanationWrapper>
)

const MultipleChoiceQuestionView = ({ data, layout, onChange }) => {
  const { setBlockFunctions } = useBlockCompletion()

  const { hideAssessmentFeedback, disableInput, templateVariables } =
    useBlocksConfig()
  const { t } = useTranslate()

  const [options, setOptions] = React.useState([])
  const [selectedOptions, setSelectedOptions] = React.useState([])
  const [isMC, setIsMC] = React.useState(false)
  const [isSurveyMode, setIsSurveyMode] = React.useState(false)
  const [showFeedback, setShowFeedback] = React.useState(false)

  const { primaryBrandColor } = useUIThemeContext()

  useEffect(() => {
    setBlockFunctions({
      validateInput,
      getBlockIsCompleted: (nextData) => isAnswerComplete(nextData, layout),
      scoreData: (data) => scoreMultipleChoice(data, layout)
    })
  }, [])

  useEffect(() => {
    const isMC = isMultipleChoice(layout, data)
    setIsMC(isMC)
  }, [layout])

  useEffect(() => {
    const selectedOptions = getQuestionSelections(data, isMC)
    setSelectedOptions(selectedOptions)
  }, [data.answer, data.options])

  useEffect(() => {
    const nextOptions = getQuestionOptions(data)
    setOptions(normalizeMCOrdering(nextOptions))

    const isSurveyMode =
      data.options.filter((o) => o.isCorrectAnswer).length === 0
    setIsSurveyMode(isSurveyMode)
  }, [data.options, layout])

  useEffect(() => {
    const showFeedback =
      !isSurveyMode && !hideAssessmentFeedback && isAnswerComplete(data)
    setShowFeedback(showFeedback)
  }, [isSurveyMode, hideAssessmentFeedback, data.answer])

  const handleChange = (value, order) => {
    const answer = updateAnswerOnSelect(
      value,
      order,
      { ...data, options: normalizeMCOrdering(data.options) },
      isMC
    )
    onChange({ answer })
  }

  const validateInput = (data) => {
    const selected = getQuestionSelections(data, isMC)

    if (!selected.length) return BLOCK_INPUT_STATES.NONE

    return BLOCK_INPUT_STATES.VALID
  }

  const OptionComponent = isMC ? RadioButton : Checkbox

  return (
    <div>
      {options.map((option, i) => {
        const isChecked = isSelected(option, selectedOptions)
        const optionFeedback = getResponseFeedbackType(option, isChecked, isMC)
        const explanationFeedback = getMCExplanationFeedback(
          option,
          isChecked,
          isMC,
          options,
          t
        )
        return (
          <OptionViewRow
            optionFeedback={showFeedback && optionFeedback}
            isChecked={isChecked}
            checkedColor={primaryBrandColor}
            key={i}
            className={`OptionViewRow${isChecked ? " is-checked" : ""}`}
          >
            {showFeedback && optionFeedback && (
              <FeedbackIndicator optionFeedback={optionFeedback} />
            )}
            <div>
              <OptionComponent
                value={option.value}
                checked={isChecked}
                disabled={disableInput || isAnswerComplete(data)}
                onChange={() => handleChange(option.value, option.order)}
              >
                <BlockText
                  html={option.label}
                  templateContext={templateVariables}
                />
              </OptionComponent>
              {showFeedback && explanationFeedback && (
                <ExplanationFeedback label={explanationFeedback} />
              )}
            </div>
          </OptionViewRow>
        )
      })}
    </div>
  )
}

export default MultipleChoiceQuestionView
